import { apiFetch, serviceBase } from './http';

/* ЛЕНТА ВИЗИТОВ ПАЦИЕНТА — `appointment-api`, пациентская половина.

       GET {gw}/appointment-api.application/api/v1/read/patient/appointment/feed

   НИ ОДНОГО ПАРАМЕТРА, и это не упрощение. Скоуп выводится только из аккаунта:
   заголовок `user-id` → карты этого аккаунта (`patient.patient.user_id`) →
   записи этих карт. `patientId` в подписи пациентской ручки был бы BOLA —
   назвать чужую карту пациент не вправе, свою не должен.

   Клиники в скоупе нет намеренно: у пациента её не бывает, он ходит к врачам
   РАЗНЫХ клиник, а `organization-id` гейтвей ему шлёт произвольный.

   ЛЕНТА ПОКАЗЫВАЕТ ВСЕ СТАТУСЫ, включая отменённые и отклонённые: это история
   отношений с клиникой, а не список дел. Убранные врачом записи (`is_archived`)
   не приезжают вовсе.

   ПОЧЕМУ ПУСТО МОЖЕТ БЫТЬ ЗАКОННО. Карта связывается с аккаунтом только двумя
   способами: пациент записался сам, или забрал ссылку на план и номер сошёлся.
   Человек, которого врач завёл руками и которому ссылку не присылал, увидит
   пустую ленту — у его карты нет аккаунта, и найти её по номеру домен не
   пытается (это был бы поиск по номеру, а не подтверждение). */

const APPOINTMENT = 'appointment-api';

/** Статусы приезжают с сервера как есть. Значение `in_progress` считается от
 *  часов, а не хранится колонкой — сервер отдаёт уже вычисленное. */
export type VisitStatus =
    | 'requested'
    | 'confirmed'
    | 'in_progress'
    | 'completed'
    | 'cancelled'
    | 'declined';

export type Visit = {
    id: string;
    startsAt: string;
    endsAt: string;
    durationMin: number;
    status: VisitStatus;
    title: string;
    /** `name`, `initials` и `clinicName` МОГУТ быть пусты: аккаунт врача
     *  заводится из claims, а при входе по телефону имени там нет вовсе. */
    doctor: { name: string; initials: string; clinicName: string };
};

export type VisitsResult =
    | { state: 'ok'; visits: Visit[] }
    /** Раздел не подключён к этому окружению (нет адреса гейтвея). */
    | { state: 'offline' }
    /** Гейтвей не пустил: узел доступа роли `patient` не выдан в дереве. */
    | { state: 'forbidden' }
    | { state: 'error' };

export async function readVisits(): Promise<VisitsResult> {
    const base = serviceBase(APPOINTMENT);
    if (base === null) return { state: 'offline' };
    try {
        const res = await apiFetch(base, '/read/patient/appointment/feed');
        if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
        if (!res.ok) return { state: 'error' };
        const body = await res.json();
        if (!Array.isArray(body)) return { state: 'error' };
        return { state: 'ok', visits: body.map(toVisit) };
    } catch {
        // Сюда же попадает отказ CORS — из браузера он неотличим от обрыва сети.
        return { state: 'error' };
    }
}

/* Разбор ЯВНЫЙ, а не `body as Visit[]`: приведение типа ничего не проверяет, и
   пропущенное поле стало бы `undefined` внутри разметки. */
function toVisit(raw: unknown): Visit {
    const r = (raw ?? {}) as Record<string, unknown>;
    const d = (r.doctor ?? {}) as Record<string, unknown>;
    return {
        id: str(r.id),
        startsAt: str(r.startsAt),
        endsAt: str(r.endsAt),
        durationMin: typeof r.durationMin === 'number' ? r.durationMin : 0,
        status: isStatus(r.status) ? r.status : 'confirmed',
        title: str(r.title),
        doctor: { name: str(d.name), initials: str(d.initials), clinicName: str(d.clinicName) },
    };
}

const STATUSES: VisitStatus[] = [
    'requested',
    'confirmed',
    'in_progress',
    'completed',
    'cancelled',
    'declined',
];
const isStatus = (v: unknown): v is VisitStatus =>
    typeof v === 'string' && (STATUSES as string[]).includes(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Прошедшее и предстоящее — ОТ ЧАСОВ, а не от статуса: отменённая завтрашняя
 *  запись всё ещё впереди, а завершённая вчерашняя — позади. Макет делит ленту
 *  ровно по этой границе. */
export function isPast(v: Visit, now: number = Date.now()): boolean {
    const t = new Date(v.startsAt).getTime();
    return Number.isNaN(t) ? false : t < now;
}
